import { RESORT_SQUARES, priceOf, square } from './board';
import { hasEffect, ownedBy, ownsFullGroup } from './queries';
import { FESTIVAL_MULT, LEVEL_RENT_MULT, MONOPOLY_MULT, RESORT_RENTS } from './rules';
import type { GameState, PlayerId } from './types';

const round5 = (n: number) => Math.max(5, Math.round(n / 5) * 5);

/** Aluguel de uma cidade pelo preço e nível, sem modificadores. */
export function cityRent(price: number, level: number): number {
  return round5(price * LEVEL_RENT_MULT[level]!);
}

/** Aluguel da casa com os multiplicadores fixos (grupo completo, festival, Campeonato), sem efeitos de carta. */
export function baseRent(s: GameState, index: number): number {
  const o = s.props[index];
  if (!o) return 0;
  const sq = square(index);
  if (sq.kind === 'resort') {
    const n = RESORT_SQUARES.filter((i) => s.props[i]?.owner === o.owner).length;
    return RESORT_RENTS[n - 1] ?? 0;
  }
  let amount = cityRent(priceOf(index), o.level);
  if (ownsFullGroup(s, o.owner, index)) amount *= MONOPOLY_MULT;
  // Cidade com Evento: mesmo preço, aluguel 1,3 vez o normal (arredondado em 5 mil).
  if (s.festivals.includes(index)) amount = round5(amount * FESTIVAL_MULT);
  if (s.championship?.square === index) amount *= s.championship.mult;
  return amount;
}

export interface RentQuote {
  amount: number;
  /** Motivo de não cobrar (interditada, Apagão), ou null. */
  waived: string | null;
}

/** Aluguel que `payer` deve ao cair nesta casa, com todos os efeitos de mesa. */
export function rentFor(s: GameState, index: number, payer: PlayerId): RentQuote {
  const o = s.props[index];
  if (!o || o.owner === payer) return { amount: 0, waived: null };
  if (hasEffect(s, (e) => e.kind === 'interdicted' && e.square === index)) return { amount: 0, waived: 'interditada' };
  if (hasEffect(s, (e) => e.kind === 'apagao')) return { amount: 0, waived: 'apagao' };
  let amount = baseRent(s, index);
  if (hasEffect(s, (e) => e.kind === 'inflacao')) amount *= 1.5;
  if (hasEffect(s, (e) => e.kind === 'viralizou' && e.player === o.owner)) amount *= 2;
  return { amount: Math.round(amount), waived: null };
}

/** Maior aluguel que o jogador cobra hoje (usado para detectar a "Facada"). */
export function maxRentCharged(s: GameState, player: PlayerId): number {
  let max = 0;
  for (const i of ownedBy(s, player)) max = Math.max(max, baseRent(s, i));
  return max;
}

/** Maior aluguel possível no tabuleiro agora (usado no nível de tensão). */
export function maxRentOnBoard(s: GameState): number {
  let max = 0;
  for (let i = 0; i < s.props.length; i++) if (s.props[i]) max = Math.max(max, baseRent(s, i));
  return max;
}

